import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { ArrowUpRight, Tv, BookOpen, Newspaper, Flame, Cross } from "lucide-react";

// Decorative gradients used when an RSS item has no image (most theology posts won't)
const PLACEHOLDERS = [
    { bg: "from-navy via-navy-700 to-navy-800", icon: BookOpen, accent: "text-gold" },
    { bg: "from-gold-500 via-gold-600 to-gold-700", icon: Cross, accent: "text-navy" },
    { bg: "from-linen via-[#EDE7D6] to-[#E5D9B4]", icon: Newspaper, accent: "text-navy" },
    { bg: "from-navy-700 via-navy to-navy-900", icon: Flame, accent: "text-gold-300" },
];

export default function OrthodoxTvSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        client.get("/orthodoxtv/latest?limit=4")
            .then(({ data }) => setItems(data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    if (!loading && items.length === 0) return null;

    return (
        <section className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">
                            <Tv className="h-3.5 w-3.5" /> In partnership with Orthodox TV
                        </div>
                        <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy tracking-tight">Voices of faith</h2>
                        <p className="mt-3 text-navy-500 font-sans max-w-xl">
                            Articles, theology, and Malankara Church news — curated from our sister publication.
                        </p>
                    </div>
                    <a
                        href="https://www.orthodoxtv.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="orthodoxtv-visit-link"
                        className="font-sans text-xs uppercase tracking-[0.18em] text-navy hover:text-gold-600 flex items-center gap-1.5 group"
                    >
                        Visit Orthodox TV
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-navy-400 font-sans">Loading articles…</div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {items.map((p, i) => {
                            const ph = PLACEHOLDERS[i % PLACEHOLDERS.length];
                            const Icon = ph.icon;
                            const isLight = ph.accent === "text-navy";
                            return (
                                <a
                                    key={p.link}
                                    href={p.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`orthodoxtv-article-${i}`}
                                    className="group bg-white border border-navy-100 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300"
                                >
                                    {p.image ? (
                                        <div className="aspect-[16/10] overflow-hidden bg-linen">
                                            <img
                                                src={p.image}
                                                alt=""
                                                loading="lazy"
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                        </div>
                                    ) : (
                                        <div className={`aspect-[16/10] relative bg-gradient-to-br ${ph.bg} flex items-center justify-center overflow-hidden`}>
                                            <Icon className={`h-16 w-16 ${ph.accent} opacity-90 transition-transform duration-500 group-hover:scale-110`} strokeWidth={1.25} />
                                            <div className={`absolute top-3 left-3 text-[9px] tracking-[0.2em] uppercase font-semibold ${isLight ? "text-navy-600" : "text-white/70"}`}>
                                                Orthodox TV
                                            </div>
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-gold-700 font-semibold">
                                            {p.date ? new Date(p.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Article"}
                                        </div>
                                        <h3 className="mt-2 font-serif text-xl text-navy leading-snug line-clamp-3 group-hover:text-gold-700 transition-colors">
                                            {p.title}
                                        </h3>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
